/**
 * CR553: Unified step row component for S1 pipeline.
 * Every S1.x and 1.n step uses this same anatomy for visual consistency.
 */
import React, { useState, useEffect, useCallback, ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, Loader2, CheckCircle, XCircle, AlertCircle, RefreshCw, Play } from "lucide-react";
import { cn } from "@/lib/utils";

export type StepRowStatus = "not_started" | "running" | "complete" | "failed" | "skipped" | "partial" | "queued" | "ready";

export interface MetricBadge {
  label: string;
  variant?: "default" | "secondary" | "outline" | "destructive";
  className?: string;
}

export interface StepRowAction {
  label: string;
  icon?: ReactNode;
  onClick: (e: React.MouseEvent) => void;
  variant?: "default" | "outline" | "destructive" | "ghost";
  disabled?: boolean;
  title?: string;
}

interface FlyerStepRowProps {
  /** Step pill text, e.g. "1.2" */
  stepNumber: string;
  /** Step pill suffix, e.g. "OCR + Signals" */
  stepLabel: string;
  /** Main title text */
  title: string;
  /** Optional lighter subtitle */
  subtitle?: string;
  /** Step status */
  status: StepRowStatus;
  /** Metric badges shown center-right */
  badges?: MetricBadge[];
  /** Whether this row is collapsible */
  collapsible?: boolean;
  /** Default open state (also overridden by running/failed) */
  defaultOpen?: boolean;
  /** localStorage persistence key */
  persistKey?: string;
  /** Primary action button */
  primaryAction?: StepRowAction;
  /** Additional action buttons */
  secondaryActions?: ReactNode;
  /** Expanded content */
  children?: ReactNode;
  /** Extra className for container */
  className?: string;
  /** Force open (e.g. when running) */
  forceOpen?: boolean;
}

// Status → icon mapping
function StatusIcon({ status }: { status: StepRowStatus }) {
  switch (status) {
    case "running":
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    case "complete":
      return <CheckCircle className="h-4 w-4 text-primary" />;
    case "failed":
      return <XCircle className="h-4 w-4 text-destructive" />;
    case "skipped":
      return <XCircle className="h-4 w-4 text-muted-foreground" />;
    case "partial":
      return <AlertCircle className="h-4 w-4 text-amber-500" />;
    case "queued":
      return <Loader2 className="h-4 w-4 text-muted-foreground" />;
    case "ready":
      return <CheckCircle className="h-4 w-4 text-muted-foreground" />;
    default:
      return null;
  }
}

// Status → badge
function StatusBadge({ status }: { status: StepRowStatus }) {
  switch (status) {
    case "running":
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary">Running</Badge>;
    case "complete":
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary">Complete</Badge>;
    case "failed":
      return <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Failed</Badge>;
    case "skipped":
      return <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Skipped</Badge>;
    case "partial":
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">Partial</Badge>;
    case "queued":
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0">Queued</Badge>;
    case "ready":
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0">Ready</Badge>;
    default:
      return null;
  }
}

function readPersisted(key: string | undefined, defaultOpen: boolean): boolean {
  if (!key) return defaultOpen;
  try {
    const v = localStorage.getItem(key);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    // Ignore localStorage read failures and fall back to default.
  }
  return defaultOpen;
}

export function FlyerStepRow({
  stepNumber,
  stepLabel,
  title,
  subtitle,
  status,
  badges = [],
  collapsible = true,
  defaultOpen = false,
  persistKey,
  primaryAction,
  secondaryActions,
  children,
  className,
  forceOpen,
}: FlyerStepRowProps) {
  // Auto-expand for running/failed
  const autoOpen = status === "running" || status === "failed" || forceOpen;
  const effectiveDefault = autoOpen || defaultOpen;

  const [isOpen, setIsOpen] = useState(() => readPersisted(persistKey, effectiveDefault));

  // Force open when status changes to running/failed
  useEffect(() => {
    if (autoOpen) setIsOpen(true);
  }, [autoOpen]);

  // Persist
  useEffect(() => {
    if (!persistKey) return;
    try {
      localStorage.setItem(persistKey, isOpen ? "1" : "0");
    } catch {
      // Ignore localStorage write failures.
    }
  }, [isOpen, persistKey]);

  const toggle = useCallback(() => {
    if (collapsible) setIsOpen(prev => !prev);
  }, [collapsible]);

  const hasExpandedContent = collapsible && children;

  return (
    <div className={cn(
      "border border-border rounded-xl bg-card transition-colors",
      hasExpandedContent && !isOpen && "hover:border-border/80",
      className
    )}>
      {/* Row header — min-h for consistent scan height */}
      <div className="flex items-center gap-0 min-h-[52px]">
        {/* Left: toggle trigger area */}
        <button
          type="button"
          className={cn(
            "flex items-center gap-2.5 flex-1 min-w-0 px-4 py-3 text-left transition-colors rounded-l-xl",
            hasExpandedContent && "hover:bg-muted/40 cursor-pointer",
            !hasExpandedContent && "cursor-default"
          )}
          onClick={toggle}
          disabled={!hasExpandedContent}
        >
          {/* Chevron (only if collapsible + has children) */}
          {hasExpandedContent ? (
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-90"
              )}
            />
          ) : (
            <div className="w-4 shrink-0" /> // spacer for alignment
          )}

          {/* Step pill */}
          <Badge variant="outline" className="text-[11px] font-mono px-2 py-0.5 shrink-0">
            {stepNumber} · {stepLabel}
          </Badge>

          {/* Status icon */}
          <StatusIcon status={status} />

          {/* Title + subtitle */}
          <span className="font-medium text-base truncate">{title}</span>
          {subtitle && (
            <span className="text-sm text-muted-foreground truncate hidden sm:inline">{subtitle}</span>
          )}

          {/* Status badge */}
          <StatusBadge status={status} />

          {/* Metric badges */}
          {badges.map((b, i) => (
            <Badge
              key={i}
              variant={b.variant || "outline"}
              className={cn("text-[11px] px-2 py-0.5 shrink-0 hidden md:inline-flex", b.className)}
            >
              {b.label}
            </Badge>
          ))}
        </button>

        {/* Right: actions area — outside toggle trigger */}
        <div
          className="flex items-center gap-2 pr-4 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {secondaryActions}
          {primaryAction && (
            <Button
              size="sm"
              variant={primaryAction.variant || "default"}
              disabled={primaryAction.disabled}
              onClick={primaryAction.onClick}
              title={primaryAction.title}
              className="gap-1.5 text-xs h-8"
            >
              {primaryAction.icon}
              {primaryAction.label}
            </Button>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {hasExpandedContent && isOpen && (
        <div className="border-t border-border/60 px-4 pb-4 pt-3">
          {children}
        </div>
      )}
    </div>
  );
}
